import React, { useState, useEffect } from 'react';
import { API_ENDPOINTS } from '../config/apiRouting';

interface RestaurantMessagingProps {
  user: any;
  storeId?: string;
}

interface Store {
  id: string;
  name: string;
  address?: string;
  enterpriseId?: string;
}

const RestaurantMessaging: React.FC<RestaurantMessagingProps> = ({ 
  user, 
  storeId: defaultStoreId = 'demo-store' 
}) => {
  const [selectedStoreId, setSelectedStoreId] = useState(defaultStoreId);
  const [stores, setStores] = useState<Store[]>([]);
  const [loadingStores, setLoadingStores] = useState(true);
  const [alertType, setAlertType] = useState('KDS_READY');
  const [message, setMessage] = useState('');
  const [priority, setPriority] = useState('medium');
  const [duration, setDuration] = useState('5_minutes');
  const [sending, setSending] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [lastResult, setLastResult] = useState<string | null>(null);

  const alertTypes = [
    { id: 'KDS_READY', label: 'Order Ready', defaultMessage: 'Table 5 order is ready for pickup' },
    { id: 'TABLE_SEATED', label: 'Table Seated', defaultMessage: 'New table has been seated at Table 3' },
    { id: 'ITEM_86', label: 'Item 86\'d', defaultMessage: 'Ribeye steak is now 86\'d for tonight' },
    { id: 'ONLINE_ORDER', label: 'Online Order', defaultMessage: 'New online order #1234 received' },
    { id: 'APPROVAL_REQUEST', label: 'Approval Needed', defaultMessage: 'Manager approval needed for void on Table 8' },
    { id: 'BROADCAST', label: 'Broadcast', defaultMessage: 'Team meeting in 15 minutes - break room' }
  ];

  const priorities = [
    { id: 'low', label: 'Low', color: 'bg-blue-100 text-blue-800' },
    { id: 'medium', label: 'Medium', color: 'bg-yellow-100 text-yellow-800' },
    { id: 'high', label: 'High', color: 'bg-orange-100 text-orange-800' },
    { id: 'urgent', label: 'Urgent', color: 'bg-red-100 text-red-800' }
  ];

  const durations = [
    { id: '5_minutes', label: '5 minutes' },
    { id: '15_minutes', label: '15 minutes' },
    { id: '1_hour', label: '1 hour' },
    { id: 'all_day', label: 'All day' }
  ];

  const handleAlertTypeChange = (newType: string) => {
    setAlertType(newType);
    const alertConfig = alertTypes.find(type => type.id === newType);
    if (alertConfig) {
      setMessage(alertConfig.defaultMessage);
    }
  };

  const clearMessages = async () => {
    setClearing(true);
    setLastResult(null);

    try {
      const response = await fetch(API_ENDPOINTS.messaging.clearStoreMessages(selectedStoreId), {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      const result = await response.json();

      if (response.ok) {
        setLastResult(`✅ Cleared ${result.deletedCount || 0} messages for this store`);
        setTimeout(() => setLastResult(null), 5000);
      } else {
        setLastResult(`❌ Failed to clear messages: ${result.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error clearing messages:', error);
      setLastResult(`❌ Network error: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setClearing(false);
    }
  };

  const sendAlert = async () => {
    if (!message.trim()) {
      setLastResult('❌ Please enter a message');
      return;
    }

    setSending(true);
    setLastResult(null);

    try {
      const response = await fetch(API_ENDPOINTS.messaging.sendAlert(), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          storeId: selectedStoreId,
          alertType,
          message: message.trim(),
          priority,
          duration,
          senderId: user?.getUsername() || 'system-user',
          senderName: user?.getUsername() || 'System User',
          metadata: {
            timestamp: new Date().toISOString(),
            source: 'enterprise-dashboard',
            persistToDatabase: true,
            targetDeviceType: 'pos'
          }
        })
      });

      const result = await response.json();

      if (response.ok) {
        setLastResult(`✅ Alert sent! Delivered to ${result.targetConnections || 0} connections`);
        // Clear the message after successful send
        setTimeout(() => setLastResult(null), 5000);
      } else {
        setLastResult(`❌ Failed to send: ${result.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error sending alert:', error);
      setLastResult(`❌ Network error: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setSending(false);
    }
  };

  // Fetch available stores
  useEffect(() => {
    const fetchStores = async () => {
      try {
        // Get auth token from user session
        let authToken = null;
        try {
          const session = await user?.getSession?.();
          authToken = session?.getIdToken?.()?.getJwtToken?.();
        } catch (e) {
          // Try alternative methods
          authToken = sessionStorage.getItem('idToken') || localStorage.getItem('idToken');
        }

        const headers: HeadersInit = {
          'Content-Type': 'application/json'
        };
        if (authToken) {
          headers['Authorization'] = `Bearer ${authToken}`;
        }

        const response = await fetch(API_ENDPOINTS.stores(), { headers });
        if (response.ok) {
          const data = await response.json();
          const rawStores = Array.isArray(data) ? data : (data.stores || data.data || []);

          // Normalize store data - API may return storeId or id, storeName or name
          const storesData = rawStores.map((store: any) => ({
            id: store.storeId || store.id,
            name: store.name || store.storeName || 'Unnamed Store',
            address: store.address || ''
          }));

          // Add demo store if not in the list
          const demoStore = { id: 'demo-store', name: 'Demo Store', address: '123 Demo St' };
          const storeExists = storesData.some((store: Store) => store.id === 'demo-store');
          if (!storeExists) {
            storesData.unshift(demoStore);
          }

          setStores(storesData);
        } else {
          console.warn('Failed to fetch stores:', response.status, response.statusText);
          // Fallback to demo store if API fails
          setStores([{ id: 'demo-store', name: 'Demo Store', address: '123 Demo St' }]);
        }
      } catch (error) {
        console.error('Failed to fetch stores:', error);
        // Fallback to demo store
        setStores([{ id: 'demo-store', name: 'Demo Store', address: '123 Demo St' }]);
      } finally {
        setLoadingStores(false);
      }
    };

    fetchStores();
  }, [user]);

  useEffect(() => {
    // Set default message for initial alert type
    const initialAlert = alertTypes.find(type => type.id === alertType);
    if (initialAlert && !message) {
      setMessage(initialAlert.defaultMessage);
    }
  }, []);

  return (
    <div className="modern-card">
      <div className="flex items-center mb-6">
        <h2 className="text-xl font-semibold text-gray-900">Restaurant Messaging</h2>
      </div>
      
      <p className="text-gray-600 mb-6">
        Send alerts and messages to restaurant staff and POS devices. Messages are stored in the database and synchronized to POS devices every 30 seconds, ensuring reliable delivery even when devices are offline.
      </p>

      {/* Store Selection */}
      <div className="form-group">
        <div className="flex items-center justify-between mb-2">
          <label className="form-label mb-0">
            Select Store/Location
          </label>
          <button
            onClick={clearMessages}
            disabled={clearing || loadingStores}
            className={`flex items-center px-3 py-1 text-xs font-medium rounded-md transition-all ${
              clearing || loadingStores
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                : 'bg-red-50 text-red-700 hover:bg-red-100 border border-red-200'
            }`}
          >
            {clearing ? (
              <>
                <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-red-600 mr-1"></div>
                Clearing...
              </>
            ) : (
              <>Clear Messages</>
            )}
          </button>
        </div>
        {loadingStores ? (
          <div className="flex items-center justify-center p-3 border border-gray-300 rounded-md bg-gray-50">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600 mr-2"></div>
            Loading stores...
          </div>
        ) : (
          <select
            value={selectedStoreId}
            onChange={(e) => setSelectedStoreId(e.target.value)}
            className="form-control"
          >
            {stores.map((store) => (
              <option key={store.id} value={store.id}>
                {store.name} {store.address && `- ${store.address}`}
              </option>
            ))}
          </select>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Alert Type Selection */}
        <div>
          <label className="form-label">
            Alert Type
          </label>
          <div className="grid grid-cols-2 gap-3">
            {alertTypes.map((type) => (
              <button
                key={type.id}
                onClick={() => handleAlertTypeChange(type.id)}
                className={`p-3 text-left rounded-lg border-2 transition-all ${
                  alertType === type.id
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="text-sm font-bold text-gray-700 mb-1">{type.id}</div>
                <div className="text-xs font-medium text-gray-500">{type.label}</div>
              </button>
            ))}
          </div>
        </div>

        {/* Message and Priority */}
        <div className="space-y-4">
          {/* Priority Selection */}
          <div>
            <label className="form-label">
              Priority
            </label>
            <div className="flex flex-wrap gap-2">
              {priorities.map((p) => (
                <button
                  key={p.id}
                  onClick={() => setPriority(p.id)}
                  className={`px-3 py-1 text-xs font-medium rounded-full transition-all ${
                    priority === p.id
                      ? p.color
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>

          {/* Duration Selection */}
          <div>
            <label className="form-label">
              Message Duration
            </label>
            <div className="flex flex-wrap gap-2">
              {durations.map((d) => (
                <button
                  key={d.id}
                  onClick={() => setDuration(d.id)}
                  className={`px-3 py-1 text-xs font-medium rounded-full transition-all ${
                    duration === d.id
                      ? 'bg-purple-100 text-purple-800'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {d.label}
                </button>
              ))}
            </div>
          </div>

          {/* Message Input */}
          <div>
            <label className="form-label">
              Message
            </label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              className="form-control"
              rows={4}
              placeholder="Enter your alert message..."
            />
          </div>

          {/* Send Button */}
          <button
            onClick={sendAlert}
            disabled={sending || !message.trim()}
            className={`w-full flex items-center justify-center px-4 py-3 rounded-lg font-medium transition-all ${
              sending || !message.trim()
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : 'btn-modern-primary'
            }`}
          >
            {sending ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Sending...
              </>
            ) : (
              <>
                Send Alert
              </>
            )}
          </button>
        </div>
      </div>

      {/* Result Display */}
      {lastResult && (
        <div className={`mt-6 p-4 rounded-lg text-sm ${
          lastResult.startsWith('✅') 
            ? 'bg-green-50 text-green-800 border border-green-200'
            : 'bg-red-50 text-red-800 border border-red-200'
        }`}>
          {lastResult}
        </div>
      )}

      {/* Info Panel */}
      <div className="mt-6 p-4 bg-gray-50 rounded-lg">
        <h4 className="text-sm font-medium text-gray-900 mb-3">System Information</h4>
        <div className="text-xs text-gray-600 space-y-2">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <p><strong>Selected Store:</strong> {selectedStoreId}</p>
              <p><strong>Store Name:</strong> {stores.find(s => s.id === selectedStoreId)?.name || 'Unknown'}</p>
              <p><strong>User:</strong> {user?.getUsername() || 'system-user'}</p>
            </div>
            <div>
              <p><strong>Available Stores:</strong> {stores.length} locations loaded</p>
              <p><strong>Sync Method:</strong> Database synchronization</p>
              <p><strong>Status:</strong> Offline-capable messaging system</p>
            </div>
          </div>
          <div className="mt-3 pt-3 border-t border-gray-200">
            <p><strong>How it works:</strong> Messages are stored in the BSO-PosMessages database table and synchronized to POS devices every 30 seconds. This ensures reliable delivery even when devices lose internet connectivity temporarily.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RestaurantMessaging;